import type { UiPhase } from "./derive-phase";

/**
 * Korean UI copy per UI phase — the single source for the right-rail hint card
 * and the in-game hint strip. Strings are taken verbatim from the wireframe
 * phase-state matrix (docs/wireframes/yinsh/specs/active-game.md) so the client
 * and the design contract can't drift.
 */
export interface PhaseCopy {
  /** Short rail label, e.g. "이동". */
  readonly label: string;
  /** One-line guidance for the current phase. */
  readonly hint: string;
}

export interface PhaseCopyContext {
  /** Rings the white player has yet to place (PLACING_RINGS only). */
  readonly whiteRingsLeft?: number;
  readonly blackRingsLeft?: number;
  /** Localised winner colour for the END hint, or null when undecided. */
  readonly winnerLabel?: string | null;
}

export function phaseCopy(
  uiPhase: UiPhase,
  ctx: PhaseCopyContext = {},
): PhaseCopy {
  switch (uiPhase) {
    case "PLACING_RINGS":
      return {
        label: "링 배치",
        hint: `빈 교차점을 눌러 링을 배치하세요. (남은: 흰 ${ctx.whiteRingsLeft ?? 0}, 검 ${ctx.blackRingsLeft ?? 0})`,
      };
    case "SELECT_RING":
      return {
        label: "이동",
        hint: "내 링(파란 후광)을 눌러 이동을 시작하세요.",
      };
    case "MOVING_RING":
      return {
        label: "이동",
        hint: "초록 점을 눌러 이동하세요. 취소는 [취소] 또는 Esc.",
      };
    case "SELECT_ROW":
      return {
        label: "점수",
        hint: "5목 점수화는 필수입니다. 가져갈 5목의 시작점과 끝점을 선택하세요.",
      };
    case "REMOVE_RING":
      return {
        label: "점수",
        hint: "트레이로 가져갈 자신의 링(주황 점선)을 선택하세요.",
      };
    case "END":
      // Visual-only rail copy: at END the GameOverModal (role="dialog") is the
      // accessible result channel, and TurnBanner — the sole live region —
      // suppresses its hint slot, so this string is never announced.
      return {
        label: "게임 종료",
        hint: ctx.winnerLabel
          ? `${ctx.winnerLabel}이 링 3개를 획득해 승리했습니다.`
          : "게임이 종료되었습니다.",
      };
  }
}

/** Shown in the rail while it's the opponent's turn. */
export const WAITING_COPY: PhaseCopy = {
  label: "대기",
  hint: "상대방의 차례를 기다리는 중…",
};
